import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import type { DaghfalClient } from '../api/client';
import type { ChildSummaryResponse, PhotoResponse, PhotoVisibilityMode } from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { Permissions } from '../auth/permissions';
import { usePermission } from '../auth/SessionProvider';
import { useI18n } from '../i18n/I18nProvider';
import { ChildAvatar } from './domain';
import { Field, Input, Select } from './Field';
import { GalleryMedia } from './GalleryMedia';
import { Badge, Button, Card, Notice, Row, Stack } from './primitives';
import { EmptyState, ErrorState, LoadingState } from './states';

type GalleryQuery = Parameters<DaghfalClient['photos']['gallery']>[0];

/** A child the form may tag: who is in the class, with the two consents that decide it (Q45). */
type Candidate = Pick<ChildSummaryResponse,
  'id' | 'firstName' | 'lastName' | 'preferredName' | 'photoUrl' | 'allergyMarks' | 'individualPhotoConsent' | 'groupPhotoConsent'>;

/**
 * A gallery staff correct (GAPS 3.14, 4.7, decision 2026-10-28 #4): each picture can be retagged and recaptioned,
 * deleted by whoever uploaded it or by someone with `photos.delete`, and, where it is withdrawn or pending,
 * published again by someone with `photos.publish`.
 *
 * What is listed, and every change, is the API's to decide: a retag of a published picture passes the consent check at
 * once and a refusal names the children, which this screen shows in the API's words.
 */
export function PhotoModeration({ query, candidates }: { query: GalleryQuery; candidates: Candidate[] }) {
  const api = useApi();
  const { t, tn, formatDate } = useI18n();

  const gallery = useQuery({ queryKey: ['photos', 'moderation', query], queryFn: () => api.photos.gallery(query) });

  if (gallery.isPending) return <LoadingState />;
  if (gallery.isError) return <ErrorState error={gallery.error} onRetry={() => void gallery.refetch()} />;
  if (gallery.data.groups.length === 0) return <EmptyState icon="📷" title={t('photos.empty')} />;

  return (
    <Stack>
      {gallery.data.groups.map((group) => (
        <Card
          key={group.date}
          title={<Row><span>{formatDate(group.date)}</span><Badge tone="count">{tn('photos.count', group.count)}</Badge></Row>}
        >
          <Stack>
            {group.photos.map((photo) => <PhotoItem key={photo.id} photo={photo} candidates={candidates} />)}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

/** One picture, with what this member of staff may do to it. */
function PhotoItem({ photo, candidates }: { photo: PhotoResponse; candidates: Candidate[] }) {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const mayEdit = usePermission(Permissions.photos.upload);
  const mayPublish = usePermission(Permissions.photos.publish);
  const mayDeleteAny = usePermission(Permissions.photos.delete);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['photos'] });

  const remove = useMutation({ mutationFn: () => api.photos.remove(photo.id), onSuccess: refresh });
  const publish = useMutation({ mutationFn: () => api.photos.publish(photo.id), onSuccess: refresh });

  const names = photo.taggedChildren.map((tag) => tag.name).join('، ');
  // A published picture's tags decide which families see it, so correcting a colleague's, like removing it, needs
  // photos.delete; an unpublished one is anyone's with photos.upload to correct (decision 2026-10-28 #4).
  const takesBack = photo.uploadedByMe || mayDeleteAny;
  const mayCorrect = mayEdit && (photo.status !== 'Published' || takesBack);

  return (
    <figure className="dg-photo dg-photo--whole" data-photo={photo.id}>
      <GalleryMedia item={photo} />
      <figcaption>
        <Stack tight>
          {photo.caption && <span>{photo.caption}</span>}
          <span className="dg-muted dg-text-sm">{t('photos.taggedNames', { names })}</span>
          <Row className="dg-row--wrap">
            {photo.occasionName && <Badge tone="neutral">{photo.occasionName}</Badge>}
            {photo.status !== 'Published' && <Badge tone="warning">{t(`photos.status.${photo.status}`)}</Badge>}
          </Row>

          {remove.isError && <ErrorState error={remove.error} />}
          {publish.isError && <ErrorState error={publish.error} />}

          {editing ? (
            <PhotoCorrectionForm photo={photo} candidates={candidates} onDone={() => setEditing(false)} />
          ) : removing ? (
            <Stack tight>
              <span className="dg-card__hint">{t('photos.removeWarning')}</span>
              <Row>
                <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>{t('photos.confirmRemove')}</Button>
                <Button variant="ghost" onClick={() => setRemoving(false)}>{t('action.cancel')}</Button>
              </Row>
            </Stack>
          ) : (
            <Row className="dg-row--wrap">
              {mayCorrect && <Button variant="ghost" onClick={() => setEditing(true)}>{t('photos.correct')}</Button>}
              {mayPublish && photo.status !== 'Published' && (
                <Button variant="secondary" busy={publish.isPending} onClick={() => publish.mutate()}>{t('photos.republish')}</Button>
              )}
              {takesBack && (
                <Button variant="ghost" onClick={() => { remove.reset(); setRemoving(true); }}>{t('photos.remove')}</Button>
              )}
            </Row>
          )}
        </Stack>
      </figcaption>
    </figure>
  );
}

/**
 * Retags, recaptions or moves a picture to another occasion or visibility. Single-child means one child, so that mode
 * offers one; shared offers every candidate, with a child the family has not agreed to greyed out, as at upload.
 */
function PhotoCorrectionForm({ photo, candidates, onDone }: { photo: PhotoResponse; candidates: Candidate[]; onDone: () => void }) {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [caption, setCaption] = useState(photo.caption ?? '');
  const [occasionId, setOccasionId] = useState<number | ''>(photo.occasionId ?? '');
  const [visibility, setVisibility] = useState<PhotoVisibilityMode>(photo.visibilityMode);
  const [tagged, setTagged] = useState<number[]>(photo.taggedChildren.map((tag) => tag.childId));

  const occasions = useQuery({ queryKey: ['occasions'], queryFn: () => api.photos.occasions() });

  // A tagged child no longer in the list (moved to another class since) stays offered, so keeping them is possible.
  const offered: { id: number; name: string; candidate?: Candidate }[] = [
    ...candidates.map((candidate) => ({
      id: candidate.id,
      name: `${candidate.preferredName ?? candidate.firstName} ${candidate.lastName}`,
      candidate,
    })),
    ...photo.taggedChildren
      .filter((tag) => !candidates.some((candidate) => candidate.id === tag.childId))
      .map((tag) => ({ id: tag.childId, name: tag.name })),
  ];
  const mayTag = (option: (typeof offered)[number]) => !option.candidate
    || (option.candidate.individualPhotoConsent && (visibility === 'SingleChild' || option.candidate.groupPhotoConsent));

  const save = useMutation({
    mutationFn: () => api.photos.update(photo.id, {
      childIds: tagged,
      caption: caption.trim(),
      occasionId: occasionId === '' ? null : occasionId,
      // "—" takes the picture off its occasion; a null occasion alone would leave it there.
      clearOccasion: occasionId === '',
      visibilityMode: visibility,
    }),
    onSuccess: async () => {
      onDone();
      await queryClient.invalidateQueries({ queryKey: ['photos'] });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (tagged.length > 0) save.mutate();
  };

  return (
    <form onSubmit={submit} noValidate>
      <Stack tight>
        {save.isError && <ErrorState error={save.error} />}
        {photo.status === 'Published' && <Notice tone="info">{t('photos.correctPublished')}</Notice>}

        <Field label={t('photos.caption')}>
          {(props) => <Input {...props} value={caption} onChange={(event) => setCaption(event.target.value)} />}
        </Field>

        <Field label={t('photos.occasion')}>
          {(props) => (
            <Select {...props} value={occasionId}
              onChange={(event) => setOccasionId(event.target.value === '' ? '' : Number(event.target.value))}>
              <option value="">—</option>
              {(occasions.data ?? []).map((occasion) => <option key={occasion.id} value={occasion.id}>{occasion.name}</option>)}
            </Select>
          )}
        </Field>

        <Field label={t('photos.visibility')} required>
          {(props) => (
            <Select {...props} value={visibility}
              onChange={(event) => {
                const next = event.target.value as PhotoVisibilityMode;
                setVisibility(next);
                if (next === 'SingleChild') setTagged((current) => current.slice(0, 1));
              }}>
              <option value="SingleChild">{t('photos.visibility.SingleChild')}</option>
              <option value="SharedTagged">{t('photos.visibility.SharedTagged')}</option>
            </Select>
          )}
        </Field>

        {visibility === 'SingleChild' ? (
          <Field label={t('photos.tagged')} required>
            {(props) => (
              <Select {...props} value={tagged[0] ?? ''}
                onChange={(event) => setTagged(event.target.value === '' ? [] : [Number(event.target.value)])}>
                <option value="">—</option>
                {offered.map((option) => (
                  <option key={option.id} value={option.id} disabled={!mayTag(option)}>
                    {mayTag(option) ? option.name : t('photos.untaggable', { name: option.name })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <fieldset className="dg-field">
            <legend className="dg-field__label">{t('photos.tagged')}</legend>
            <Stack tight>
              {offered.map((option) => (
                <label key={option.id} className="dg-checkbox" aria-disabled={!mayTag(option)}>
                  <input
                    type="checkbox"
                    disabled={!mayTag(option) && !tagged.includes(option.id)}
                    checked={tagged.includes(option.id)}
                    onChange={(event) => setTagged((current) => event.target.checked
                      ? [...current, option.id]
                      : current.filter((value) => value !== option.id))}
                  />
                  {option.candidate && (
                    <ChildAvatar child={option.candidate} photoUrl={option.candidate.photoUrl} size="sm"
                      allergies={option.candidate.allergyMarks} />
                  )}
                  <span>
                    {option.name}
                    {option.candidate && !option.candidate.individualPhotoConsent && <Badge tone="neutral">{t('photos.noConsent')}</Badge>}
                    {option.candidate?.individualPhotoConsent && !option.candidate.groupPhotoConsent && (
                      <Badge tone="neutral">{t('photos.noGroupConsent')}</Badge>
                    )}
                  </span>
                </label>
              ))}
            </Stack>
          </fieldset>
        )}

        <Row>
          <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')} disabled={tagged.length === 0}>
            {t('action.save')}
          </Button>
          <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </form>
  );
}
